import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Edit, X, Flame, CloudRain, Zap, AlertTriangle, MoreVertical } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { type Crisis } from '@/lib/types'
import { CrisisEditForm, CloseCrisisDialog } from './index'
import { useCrisisStore } from '@/lib/stores/crisisStore'
import { useIsMobile } from '@/lib/hooks/useMediaQuery'

interface CrisisTopbarProps {
  crisis: Crisis
}

const getCrisisIcon = (type: string) => {
  const lowerType = type.toLowerCase()
  if (lowerType.includes('fire')) return Flame
  if (lowerType.includes('flood') || lowerType.includes('water')) return CloudRain
  if (lowerType.includes('power') || lowerType.includes('electric')) return Zap
  return AlertTriangle
}

const formatDuration = (seconds: number): string => {
  const days = Math.floor(seconds / 86400)
  const remainingAfterDays = seconds % 86400
  const hours = Math.floor(remainingAfterDays / 3600)
  const minutes = Math.floor((remainingAfterDays % 3600) / 60)
  const secs = remainingAfterDays % 60

  if (days > 0) {
    return `${days}d ${hours}h ${minutes}m ${secs}s`
  }
  if (hours > 0) {
    return `${hours}h ${minutes}m ${secs}s`
  }
  if (minutes > 0) {
    return `${minutes}m ${secs}s`
  }
  return `${secs}s`
}

export function CrisisTopbar({ crisis }: CrisisTopbarProps) {
  const { t } = useTranslation()
  const [showEditForm, setShowEditForm] = useState(false)
  const [showCloseDialog, setShowCloseDialog] = useState(false)
  const [currentDuration, setCurrentDuration] = useState(0)
  const isMobile = useIsMobile()

  const Icon = getCrisisIcon(crisis.type)
  const isClosed = crisis.status === 'closed'
  const isPractice = !!crisis.practiceCaseId
  const { clearActiveCrisis } = useCrisisStore()

  useEffect(() => {
    const updateDuration = () => {
      const startTime = new Date(crisis.createdAt).getTime()
      const endTime = isClosed && crisis.closedAt ? new Date(crisis.closedAt).getTime() : Date.now()
      const durationSeconds = Math.floor((endTime - startTime) / 1000)
      setCurrentDuration(durationSeconds)
    }

    // Update immediately
    updateDuration()

    // Update every second (only if active)
    const interval = isClosed ? null : setInterval(updateDuration, 1000)

    return () => {
      if (interval) clearInterval(interval)
    }
  }, [crisis.createdAt, crisis.closedAt, isClosed])

  const handleEdit = () => {
    setShowEditForm(true)
  }

  const handleClose = () => {
    if (isClosed) {
      // If the crisis is already closed, clear the selected crisis and go back to crisis selection
      clearActiveCrisis()
      // Use full page redirect to ensure CrisisGuard and routing reset
      if (typeof window !== 'undefined') {
        window.location.href = '/crisis/select'
      }
      return
    }

    setShowCloseDialog(true)
  }

  const isClosedCrisis = crisis.status === 'closed'

  // Mobile layout
  if (isMobile) {
    return (
      <>
        <div
          className="sticky top-0 z-40 border-b border-l-4 border-l-red-700 bg-red-100 shadow-sm"
          role="banner"
          aria-label="Actieve crisis informatie"
        >
          <div className="flex items-center justify-between p-3">
            {/* Left: Icon + Type + Duration */}
            <div className="flex items-center gap-2 flex-1 min-w-0 text-left">
              <Icon className="h-6 w-6 text-gray-900 flex-shrink-0" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <div className="font-bold text-sm text-gray-900 truncate">
                  {crisis.type} | {crisis.location}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <div className="text-md font-mono text-gray-900">
                    {formatDuration(currentDuration)}
                    {isClosed && ' (Gesloten)'}
                  </div>
                  {isPractice && (
                    <span className="inline-block px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 border-blue-200">
                      Oefenscenario
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Right: Actions Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  className="ml-2 flex-shrink-0"
                  aria-label="Crisis acties menu"
                >
                  <MoreVertical className="h-5 w-5 text-gray-900" aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-white">
                {!isClosed && (
                  <DropdownMenuItem onClick={handleEdit} className="bg-white">
                    <Edit className="mr-2 h-4 w-4" />
                    {t('crisis.editCrisis')}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={handleClose} className="bg-white">
                  <X className="mr-2 h-4 w-4" />
                  {isClosed ? t('crisis.leaveCrisis') : t('crisis.closeCrisis')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <CrisisEditForm crisis={crisis} open={showEditForm} onOpenChange={setShowEditForm} />

        <CloseCrisisDialog open={showCloseDialog} onOpenChange={setShowCloseDialog} />
      </>
    )
  }

  // Desktop layout (original)
  return (
    <>
      <div
        className={'border-l-4 shadow-md border-l-red-700 bg-red-100 dark:bg-red-200'}
        role="banner"
        aria-label="Actieve crisis informatie"
      >
        <div className="flex items-center justify-between px-6 py-1">
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-3">
              <div className="p-1 rounded-lg">
                <Icon className="h-8 w-8 text-gray-900 dark:text-gray-900" aria-hidden="true" />
              </div>
              <div className="font-extrabold text-gray-900 dark:text-gray-900 text-lg">
                {crisis.type}
              </div>
              <span>-</span>
              <div className="text-sm text-gray-900 dark:text-gray-900 font-semibold">
                {crisis.location}
              </div>
            </div>
            <div className="hidden md:block w-px h-6 bg-red-200 dark:bg-red-800"></div>

            <div className="flex items-center space-x-3">
              <div className="text-xs text-gray-900 dark:text-gray-900 uppercase tracking-wide font-bold">
                {isClosed ? 'Duur' : 'Actief sinds'}
              </div>
              <div className="text-l font-mono font-extrabold text-gray-900 dark:text-gray-900 tabular-nums">
                {formatDuration(currentDuration)}
              </div>
              {isPractice && (
                <div className="mt-1 px-2 py-0.5 text-xs font-semibold bg-blue-200 border-blue-700 text-blue-700 rounded-full">
                  Oefenscenario
                </div>
              )}
              {isClosed && (
                <div className="mt-1 px-2 py-0.5 text-xs font-semibold bg-gray-600 text-white rounded-full">
                  GESLOTEN
                </div>
              )}
            </div>
            {isClosedCrisis && (
              <>
                <div className="hidden md:block w-px h-12 bg-red-200 dark:bg-red-800"></div>

                <div className="flex flex-col items-center">
                  <div className="mx-6 p-3">
                    <div className="flex items-center">
                      <div>
                        <p className="font-semibold">{t('crisis.readOnly')}</p>
                        <p className="text-sm">{t('crisis.crisisClosed')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="flex items-center space-x-3">
            {!isClosed && (
              <Button
                variant="link"
                size="sm"
                onClick={handleEdit}
                disabled={isClosed}
                className="text-gray-900 hover:text-gray-500 underline cursor-pointer font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Crisis bewerken"
              >
                <Edit className="h-4 w-4 mr-2" aria-hidden="true" />
                <span className="hidden sm:inline">{t('crisis.editCrisis')}</span>
              </Button>
            )}

            <Button
              variant="link"
              size="sm"
              onClick={handleClose}
              className="text-gray-900 hover:text-gray-500 font-semibold underline cursor-pointer  disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label={isClosed ? 'Crisis verlaten' : 'Crisis sluiten'}
            >
              <X className="h-4 w-4 mr-2" aria-hidden="true" />
              <span className="hidden sm:inline">
                {isClosed ? t('crisis.leaveCrisis') : t('crisis.closeCrisis')}
              </span>
            </Button>
          </div>
        </div>
      </div>

      <CrisisEditForm crisis={crisis} open={showEditForm} onOpenChange={setShowEditForm} />

      <CloseCrisisDialog open={showCloseDialog} onOpenChange={setShowCloseDialog} />
    </>
  )
}
